import React, { useEffect } from 'react';
import { useToast, ToastContainer } from '../ui/Toast';
import { useAuth } from '../auth/AuthProvider';

interface ApiErrorDetail {
  status?: number;
  userMessage: string;
  correlationId?: string;
}

const GlobalApiErrorListener: React.FC = () => {
  const { toast, toasts, removeToast } = useToast();
  const { connectivity } = useAuth();

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ApiErrorDetail>).detail;
      if (!detail) return;
      const { status, userMessage, correlationId } = detail;
      // Gate: surface global toasts for network errors (no status) and 5xx.
      // Let feature pages handle 4xx locally.
      if (typeof status === 'number' && status < 500) return;
      // If the app is already in degraded mode, avoid spamming toasts; the global banner covers it.
      if (connectivity === 'degraded') return;

      const shortRef = correlationId ? ` (Ref: ${String(correlationId).slice(0, 8)})` : '';
      const description = `${userMessage}${shortRef}`;

      const onDetails = async () => {
        const full = correlationId || 'N/A';
        try {
          await navigator.clipboard.writeText(full);
          toast.info('Reference copied. Contact support with this reference.');
        } catch {
          // Fallback if clipboard not available
          alert(`Correlation ID: ${full}\n\nContact support with this reference.`);
        }
      };

      toast.error(description, {
        action: correlationId
          ? { label: 'Details', onClick: onDetails }
          : undefined,
        duration: 7000,
      });
    };
    window.addEventListener('api-error', handler as EventListener);

    // Handle generic unhandled rejections (e.g., fetch network errors)
    const rejHandler = (ev: PromiseRejectionEvent) => {
      const reason = ev.reason;
      const msg = (reason && (reason.message || String(reason))) || '';
      // Only show for typical network failures
      if (/Failed to fetch|NetworkError|TypeError/i.test(msg)) {
        // If already degraded, skip toast (banner is visible)
        if ((window as any).__connectivity === 'degraded') return;
        toast.error('Unable to reach the server. Please check your network connection and try again.', {
          duration: 7000,
        });
      }
    };
    window.addEventListener('unhandledrejection', rejHandler);

    return () => {
      window.removeEventListener('api-error', handler as EventListener);
      window.removeEventListener('unhandledrejection', rejHandler);
    };
  }, [toast, connectivity]);

  // Host a toast container for global API errors
  return <ToastContainer toasts={toasts} onRemove={removeToast} />;
};

export default GlobalApiErrorListener;
